import { ApiError, errorMessage } from '@/domains/api';
import type { IRegistrationView } from '../types';
import { usStateName } from '../us-states';

/**
 * The words the registration screens put around what survey-api sends, keyed by the codes it
 * sends — a survey code, an identifier type, a field name — and deciding nothing by them.
 *
 * **Every map falls back to the code itself**, never to a refusal or a blank: the view lists
 * what the run may carry (`idTypes`, `fields`) from survey-api's own catalogue, and a name
 * this build has no words for is still one the server will accept. survey-api says outright
 * that it ignores an unknown field name rather than refusing it, so that a stale client cannot
 * stop somebody registering; a client that hid the field would be doing exactly that from the
 * other side.
 */

/** A registration by what it registers, in the chooser's words — "the financial advisor
 *  registration" — where a screen has to name two of them apart. */
const REGISTRATION_NAMES: Record<string, string> = {
  FA: 'financial advisor',
  FSP: 'insurance professional',
  RIA_FIRM: 'RIA firm',
  TEAMS: 'team'
};

export function registrationName(survey: string): string {
  return REGISTRATION_NAMES[survey] ?? survey;
}

/** A person's run by its numbers, in the Add page's words — "the CRD registration". */
const NUMBER_NAMES: Record<string, string> = {
  crd: 'CRD',
  insurance: 'NPN or licence number'
};

export function numbersName(numbers: readonly string[]): string {
  return numbers.map((number) => NUMBER_NAMES[number] ?? number).join(' and ');
}

/** `409 several-persons` (PFR D16), at the start or the completion of a number added signed in:
 *  the account acts for more than one person, and staff add the number (Q12). The Help link
 *  follows it. */
export const SEVERAL_PERSONS =
  'This account acts for more than one person, so a number cannot be added here. Tell us the ' +
  'number and we will add it for you.';

/** A roster role, as a sighting names it (survey-api's `MemberRoles`) — mid-sentence, a role
 *  this build has no words for as spelled. */
const SIGHTING_ROLES: Record<string, string> = {
  lead: 'lead advisor',
  member: 'team member',
  principal: 'principal'
};

export function sightingRole(role: string): string {
  return SIGHTING_ROLES[role] ?? role;
}

const REGISTRIES: Record<string, string> = {
  FA: 'FINRA BrokerCheck',
  FSP: 'NIPR',
  RIA_FIRM: 'the SEC',
  // A team anchors on its lead advisor's CRD, which survey-api asks FINRA about as a person's.
  TEAMS: 'FINRA BrokerCheck'
};

/** Who is asked whether the number is real — mid-sentence, since one of them is "the SEC". It is
 *  also who an `error` is about: survey-api asks the SEC's IAPD beside BrokerCheck about a CRD
 *  (its SR-396 B1), but the pair's `error` is BrokerCheck's alone (B4), so "could not check"
 *  names BrokerCheck and nothing else (the owner's Q2). */
export function registryLabel(survey: string): string {
  return REGISTRIES[survey] ?? 'the registry';
}

/**
 * Who answered, where the answer is the pair's — a match or a miss. survey-api's SR-396 asks the
 * SEC's IAPD beside FINRA's BrokerCheck about a CRD, and the pair's status is `ok` if either
 * answers `ok`, else BrokerCheck's (its B4): so a name or a match can be IAPD's, and a miss is
 * both registries'. The owner's Q2 names the pair wherever that is true of it; a survey not
 * listed here is answered by `registryLabel`'s registry alone.
 */
const ANSWERED_BY: Record<string, string> = {
  FA: 'FINRA BrokerCheck or the SEC’s IAPD',
  TEAMS: 'FINRA BrokerCheck or the SEC’s IAPD'
};

export function answeredBy(survey: string): string {
  return ANSWERED_BY[survey] ?? registryLabel(survey);
}

/**
 * What a registry's `not_found` says, as a sentence without its full stop — both screens that
 * report a miss open with it.
 *
 * The SEC's is narrower than "no record", and has to be: survey-api matches **registered**
 * advisers only (its SEC ADV plan, R7), so an exempt reporting adviser's `802-` number — one
 * the SEC does hold — answers `not_found` like a number it never issued, and "we found no
 * record of that number" would be false about it.
 */
const MISSES: Record<string, string> = {
  RIA_FIRM: 'The SEC does not list that number as a registered investment adviser',
  TEAMS: 'Neither FINRA BrokerCheck nor the SEC’s IAPD lists an advisor under that CRD number'
};

export function missStatement(survey: string): string {
  return MISSES[survey] ?? `We found no record of that number at ${answeredBy(survey)}`;
}

const ID_TYPES: Record<string, string> = {
  CRD: 'CRD number',
  NPN: 'NAIC NPN number',
  INSURANCE_LICENSE: 'Insurance licence number',
  SEC_NUMBER: 'Firm SEC number',
  // No registration anchors on it; the identifier card on the catalogue can be shown one
  // (roadmap D3 split the migrated firms' CRDs off into it).
  ORG_CRD: 'Firm CRD number',
  // What a team holds its lead's CRD as. A registration types it as a `CRD` — the view's
  // `idTypes` is `['CRD']` for TEAMS — so this label is the admin screens' and the catalogue's.
  TEAM_LEAD_CRD: 'Lead advisor CRD number'
};

/**
 * The words for an identifier type, for both screens that name one: the anchor step here, and
 * the identifier card on the catalogue (`domains/survey`), which is why the auth barrel exports
 * it. One map, so a registrant is never asked for a "CRD number" and then shown it back under a
 * different name. A type with no words here — the migration's `LEGACY_USER_ID`, say — renders
 * as its code.
 */
export function idTypeLabel(idType: string): string {
  return ID_TYPES[idType] ?? idType;
}

/**
 * The anchor field's label, which is `idTypeLabel` except where the survey says whose number it
 * is: a team types its **lead advisor's** CRD, and "CRD number" alone would read as the
 * registrant's own. Survey-aware here rather than in `ID_TYPES`, whose `CRD` is a person's CRD
 * wherever else it is shown.
 */
export function anchorLabel(survey: string, idType: string): string {
  if (survey === 'TEAMS' && idType === 'CRD') return 'Lead advisor’s CRD number';
  return idTypeLabel(idType);
}

/** The two anchors that are digits alone. A keyboard, not a rule: nothing refuses a letter,
 *  and an SEC number (`801-…`) or a state licence would be untypable on a number pad. */
const NUMERIC_ID_TYPES = new Set(['CRD', 'NPN']);

export function idTypeInputMode(idType: string): 'numeric' | 'text' {
  return NUMERIC_ID_TYPES.has(idType) ? 'numeric' : 'text';
}

/**
 * What a number of each type looks like, shown in the empty field — for the one type whose
 * shape survey-api refuses any other value for: an SEC number (`SecNumbers`, its SEC ADV
 * block). The example is the server's own, from the refusal's copy, so the field and the
 * refusal under it show one number.
 *
 * A hint and not a rule. Nothing here checks the value against it: the server also takes the
 * prefix without its dash, stray spaces and leading zeros, and a pattern copied here would
 * have to agree with the .NET one on every one of those — the second evaluator CLAUDE.md forbids.
 */
const PLACEHOLDERS: Record<string, string> = {
  SEC_NUMBER: '801-12345'
};

export function idTypePlaceholder(idType: string): string | undefined {
  return PLACEHOLDERS[idType];
}

/**
 * The step back to the number (`/revision`): "Enter a different number" — or, for a team that
 * said its lead has no CRD and so gave none, the way to give one after all. A run past the
 * anchor has no lookup only then, since every number submitted is looked up.
 */
export function revisionLabel(view: IRegistrationView): string {
  return view.lookup === null ? 'Enter a CRD number' : 'Enter a different number';
}

const FIELDS: Record<string, string> = {
  // A person's run's two (SR-487), in page 08's words.
  legalFirstName: 'First name',
  legalLastName: 'Last name',
  firmName: 'Official firm name',
  teamName: 'Team name'
};

export function fieldLabel(name: string): string {
  return FIELDS[name] ?? name;
}

/**
 * The per-field messages of a `400 ValidationProblem`, keyed as survey-api keyed them — or
 * none, for any other error.
 *
 * Rendered as sent and judged nowhere: which fields are required (a new participant needs *a*
 * name part, not each one; a participant we already hold needs none) and how long a number may
 * be are the server's to state, and a copy of either here would be the second evaluator
 * CLAUDE.md forbids.
 */
export function validationErrors(error: unknown): Record<string, string[]> {
  return error instanceof ApiError && error.status === 400 ? (error.problem?.errors ?? {}) : {};
}

/**
 * What a refusal says that no field on the step carries: the per-field messages for names
 * the step draws no field for, or — for a refusal with none at all, a 500 or the server out of
 * reach — the error as one line. Null when every message already sits beside its field.
 */
export function unplacedFailure(
  error: unknown,
  placed: ReadonlySet<string>,
  fallback: string
): Nullable<string> {
  if (!error) return null;
  const problems = Object.entries(validationErrors(error));
  if (problems.length === 0) return errorMessage(error, fallback);
  const unplaced = problems.filter(([name]) => !placed.has(name));
  if (unplaced.length === 0) return null;
  return unplaced.map(([name, messages]) => `${name}: ${messages.join(' ')}`).join('; ');
}

/**
 * The front door's words for a person's run (SR-487), page by page of the client's wireframes
 * ("Proposed sign-up flow", 2026-10-28). **Each screen says what its page says** — heading, lines,
 * labels, rows, buttons and links, nothing added (the owner, 2026-10-29) — and a line that is not
 * the page's is the design's register's, with its reason (`docs/superpowers/specs/
 * 2026-10-29-person-first-registration-ui.md`). Spelled as the wireframes spell them: "license",
 * where the rest of the app writes "licence".
 */
export const DOOR = {
  /** 01 — which numbers the registrant holds. */
  choose: {
    title: 'Let’s get you set up',
    lede:
      'Select any you hold. We use it to verify your identity and to fill in as much of your ' +
      'survey as we can.',
    crd: { label: 'Individual CRD', hint: 'Your own number issued by FINRA, not your firm’s' },
    insurance: { label: 'NPN or license number', hint: 'National Producer Number issued by NIPR' },
    none: {
      label: 'None of the above',
      hint: 'You can still create an account and work on a survey'
    }
  },
  /** 02 — the CRD. `retry` is FINRA's first miss, which keeps the run here for one more try
   *  (the owner, 2026-10-29: the wireframe draws no such state). `addedLede` is the page signed
   *  in, at `/portal/surveys/add`, where the account exists already. */
  crd: {
    title: 'Your CRD number',
    lede:
      'We look it up on BrokerCheck and show you what we find. Your account is created at the ' +
      'last step.',
    addedLede:
      'We look it up on BrokerCheck and show you what we find. It is added to your account at ' +
      'the last step.',
    label: 'CRD number',
    retry:
      'We found no record of that number at FINRA BrokerCheck. Check it and press Continue again.'
  },
  /** 03 — FINRA's record of the CRD. */
  finraRecord: {
    title: 'Is this you?',
    lede: (crd: string) => `This is what FINRA holds against CRD ${crd}.`,
    rows: {
      name: 'Name',
      crd: 'CRD',
      firm: 'Current firm',
      firmCrd: 'Firm CRD',
      registeredSince: 'Registered since'
    },
    source: 'From FINRA BrokerCheck, retrieved today',
    yes: 'Yes, that’s me',
    no: 'No, that’s not me'
  },
  /** 04 — the number is another account's. "An invitation to sign in" where the wireframe says
   *  "a password reset": Invent's IdP holds the password. The Help link ends the no-access line
   *  where the wireframe names an address (Q3, Q9); where no invitation went, no address is
   *  named, and the line is `moveAccount` alone (Q25). */
  taken: {
    title: 'You already have an account',
    lede: (number: string) => `${number} is registered to an account with us.`,
    unknownNumber: 'That number',
    noticeTitle: 'We have sent an invitation to sign in',
    onItsWay: 'It is on its way to',
    followLink: 'Follow the link in that email to get back in.',
    noAccess: 'No longer have access to that address?',
    moveAccount:
      'Tell us and we will move your account across for you. You cannot change it here.',
    startAgain: 'Start again'
  },
  /** 05 — an NPN, or a state licence with its state. "Give us one of them" holds both typed
   *  (decision 5). No line about checking it: nothing is looked up (PFR D8). */
  insurance: {
    title: 'Your producer number',
    lede:
      'We only need one of these. Give us your NPN if you have it — otherwise your state ' +
      'license number, and we will find the NPN from that.',
    npn: 'National Producer Number',
    or: 'or',
    license: 'State license number',
    licensePlaceholder: 'License number',
    state: 'State',
    statePlaceholder: 'Select a state',
    oneOfThem: 'Give us one of them.'
  },
  /** 06 — our own record of an insurance number, drawn only where we hold it: no NIPR (PFR D8),
   *  so no licence status or lines of authority. */
  ourRecord: {
    title: 'Is this you?',
    lede: (number: string) => `This is what we hold against ${number}.`,
    rows: { name: 'Name', npn: 'NPN', license: 'License number', state: 'State' },
    source: 'From our records'
  },
  /** 07 — no number to give, drawn before any run starts. */
  none: {
    title: 'That’s fine',
    lede: 'Plenty of the people who complete a survey hold no registration number of their own.',
    noticeTitle: 'You can still do everything you need to',
    notice:
      'Finish a pre-existing Advisor/FSP survey or create a team or firm survey. If you are listed ' +
      'on any pre-existing surveys, they will be waiting when you sign in.'
  },
  /** 08 — the account. No password: Invent's IdP holds it. */
  create: {
    title: 'Create your account',
    lede: 'Last step. We will send a link to confirm your address.',
    email: 'Work email',
    submit: 'Create account',
    busy: 'Creating your account…'
  },
  /** 09 — the invitation to sign in, on its way. "Send it again" asks for it once more; its
   *  status line is the owner's (2026-10-29), as is the wrong address's Help link at the end. */
  checkEmail: {
    title: 'Check your email',
    again: 'Send it again',
    sending: 'Sending…',
    sentAgain: 'We have sent it again.',
    wrongAddress: 'Wrong address? Tell us and we will correct it.'
  },
  continue: 'Continue',
  checking: 'Checking…',
  back: 'Back',
  /** Where a record's row is empty: drawn, never dropped (decision 4). */
  empty: '—'
} as const;

/** A number as pages 04 and 06 name it: "CRD 4821906", "NPN 18294471", "license number 7-11"
 *  (`capital` where it opens a sentence) — by the type it was sent as. */
export function enteredNumberText(
  number: { value: string; idType: string },
  capital = false
): string {
  if (number.idType === 'INSURANCE_LICENSE') {
    return `${capital ? 'License' : 'license'} number ${number.value}`;
  }
  return `${number.idType === 'NPN' ? 'NPN' : 'CRD'} ${number.value}`;
}

/** A number added signed in, as My surveys names it (the owner's Q11): "CRD 1234567", and a
 *  licence with its state, "License number 99-1 (California)". */
export function addedNumberText(number: {
  value: string;
  idType: string;
  issuingState?: string;
}): string {
  const text = enteredNumberText(number, true);
  return number.issuingState ? `${text} (${usStateName(number.issuingState)})` : text;
}

/** "Registered since" as page 03 draws it — "March 2009" — from survey-api's ISO date; null for
 *  none, and for a value that is no date. */
export function registeredSinceText(date: Nullable<string> | undefined): Nullable<string> {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' });
}

/**
 * Page 09's "It expires in …": the whole days left until the invitation's expiry, counted up so a
 * seven-day invitation read the moment it was made says seven — or null where the completion
 * named no expiry (nothing was queued) or the moment has passed, and the line is left out.
 */
export function expiryText(
  expiresAt: Nullable<string> | undefined,
  now = Date.now()
): Nullable<string> {
  if (!expiresAt) return null;
  const left = Date.parse(expiresAt) - now;
  if (Number.isNaN(left) || left <= 0) return null;
  const days = Math.ceil(left / 86_400_000);
  return `It expires in ${days} ${days === 1 ? 'day' : 'days'}.`;
}

/** The RIA door's notice title — ours, above survey-api's `notice.text`. */
export const RIA_DOOR_TITLE = 'Only SEC-registered firms take this survey';

/**
 * The RIA-vs-Teams criteria under the RIA door's notice — this app's words, drafted from Teams'
 * routing tree (survey-api's `TeamGating`) for the owner to read in the merge request (the block
 * plan's Q12 (a)): survey-api leaves them to the client (RL §4), and the workbook's "Display RIA
 * vs. Teams Criteria" writes none out.
 */
export const RIA_VS_TEAMS: readonly string[] = [
  'An investment adviser firm registered with the SEC registers as the firm, by its SEC number.',
  'A firm registered with a state, or exempt from SEC registration, cannot register as a firm ' +
    'here. Its advisors can register as a team.',
  'A team inside a broker-dealer or a wirehouse, a team affiliated with a trust company, and an ' +
    'independent practice register as a team, by the lead advisor’s CRD number.'
];
